import type { BookedFlightItineraryWithDepartureTime } from "@b2bportal/air-booking-api";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import { useI18nContext } from "@hopper-b2b/i18n";
import { FlightDisruptionAlertBanner } from "@tenant/disruption";
import clsx from "clsx";
import styles from "./FlightDetailsPanel.module.scss";
import { FlightSegmentCard } from "./FlightSegmentCard";
import { useFlightDetailsPanel } from "./useFlightDetailsPanel";
import { useFlightDisruptionAlertBanner } from "@/hooks/shared/useFlightDisruptionAlertBanner";

interface FlightDetailsPanelProps {
  className?: string;
  itinerary: BookedFlightItineraryWithDepartureTime;
  airportMap?: Record<
    string,
    { name: string; code: string; cityName?: string }
  >;
  airlineMap?: Record<string, { displayName: string }>;
  hideLayoverButton?: boolean;
  stackCards?: boolean;
}

export const FlightDetailsPanel = ({
  className,
  itinerary,
  airportMap,
  airlineMap,
  hideLayoverButton = false,
  stackCards = false,
}: FlightDetailsPanelProps) => {
  const [block, cn] = useModuleBEM(styles, "FlightDetailsPanel");
  const { t } = useI18nContext();
  const { sliceSummaries, title } = useFlightDetailsPanel({
    itinerary,
    airlineMap,
  });

  const {
    handleDisruptionProtectionCta,
    disruptionAlertBannerType,
    disruptionBannerDestinationProps,
    showDisruptionContractBanner,
  } = useFlightDisruptionAlertBanner(itinerary, airportMap);

  return (
    <div className={clsx(block, className)}>
      <div className={cn("header")}>
        <h2 className={cn("title")}>{title}</h2>
      </div>
      {showDisruptionContractBanner && (
        <FlightDisruptionAlertBanner
          onClick={handleDisruptionProtectionCta}
          type={disruptionAlertBannerType}
          bannerIcon={IconNameEnum.warningCircle}
          productName={t("core-disruption.productName")}
          {...disruptionBannerDestinationProps}
        ></FlightDisruptionAlertBanner>
      )}
      <div className={cn("cards", { stacked: stackCards })}>
        {sliceSummaries.length > 0 &&
          sliceSummaries.map(
            (summary, index) =>
              summary && (
                <FlightSegmentCard
                  key={`${summary.firstSegment.origin?.locationCode}-${summary.firstSegment.scheduledDeparture}-${index}`}
                  segment={summary.firstSegment}
                  airportMap={airportMap}
                  airlineMap={airlineMap}
                  lastSegment={summary.lastSegment}
                  totalDurationMinutes={summary.totalDurationMinutes}
                  stopsCount={summary.stopsCount}
                  displayDestinationCode={summary.displayDestinationCode}
                  className={cn("card")}
                />
              )
          )}
      </div>
    </div>
  );
};
